/**
 * Copy the signed-in person's claims (name, roles) to the platform once per sign-in, so they
 * appear by name in the assignee picker (owner ruling DEC-102, `docs/adr/9040`).
 *
 * `POST /api/v1/me/session` (bata-platform #496): no body, idempotent, an `Idempotency-Key` like
 * every POST, 200 with the profile. Until it is pinned `SESSION_CLAIMS_SYNC` is `null`
 * (`src/api/pending/alert-groups.ts`) and nothing is sent — the house pattern for a route the
 * pinned spec does not carry (ADR 9037). Once set, it goes out once per signed-in subject per
 * browser session, with ONE key for that session (a retry after a failure re-sends the same key,
 * so the platform records one act): both remembered in `sessionStorage` inside `try`/`catch` —
 * storage may be unavailable, and then it is once per page load — and in memory. A failure never
 * blocks the app: it is logged and the app continues; the next page load tries again.
 */

import { useEffect } from 'react';

import { callOperation, newIdempotencyKey } from '../api';
import { SESSION_CLAIMS_SYNC, type SessionClaimsSyncOp } from '../api/pending/alert-groups';
import { useAccessGrants } from './grants';

/** The `sessionStorage` key for a subject already synced this session. */
export function claimsSyncKey(subject: string): string {
  return `bata.claims-synced.${subject}`;
}

/** The `sessionStorage` key holding this session's idempotency key for a subject. */
export function claimsIdempotencyKey(subject: string): string {
  return `bata.claims-key.${subject}`;
}

const syncedInMemory = new Set<string>();
const keysInMemory = new Map<string, string>();

/** The session's one idempotency key for this subject: minted once, reused on every retry. */
function sessionKey(subject: string): string {
  const held = keysInMemory.get(subject);
  if (held !== undefined) return held;
  let key: string | null;
  try {
    key = window.sessionStorage.getItem(claimsIdempotencyKey(subject));
  } catch {
    key = null;
  }
  if (key === null) {
    key = newIdempotencyKey();
    try {
      window.sessionStorage.setItem(claimsIdempotencyKey(subject), key);
    } catch {
      // Storage refused: the in-memory key holds for this page load.
    }
  }
  keysInMemory.set(subject, key);
  return key;
}

function alreadySynced(subject: string): boolean {
  if (syncedInMemory.has(subject)) return true;
  try {
    return window.sessionStorage.getItem(claimsSyncKey(subject)) !== null;
  } catch {
    return false;
  }
}

function markSynced(subject: string): void {
  syncedInMemory.add(subject);
  try {
    window.sessionStorage.setItem(claimsSyncKey(subject), new Date().toISOString());
  } catch {
    // Storage refused: the in-memory mark holds for this page load.
  }
}

/** Forget every mark — for a test, and on sign-out. */
export function resetClaimsSync(): void {
  syncedInMemory.clear();
  keysInMemory.clear();
}

/**
 * Send the claims once for `subject` through `op`. Resolves `true` when a write went out (and
 * succeeded), `false` when there was nothing to do. Exported for the test.
 */
export async function syncClaimsOnce(op: SessionClaimsSyncOp | null, subject: string | null): Promise<boolean> {
  if (op === null || subject === null || alreadySynced(subject)) return false;
  syncedInMemory.add(subject);
  try {
    await callOperation(op, {}, { idempotencyKey: sessionKey(subject) });
    markSynced(subject);
    return true;
  } catch (error) {
    // Never blocks the app: said in the console, and tried again on the next page load.
    console.warn('session claims sync failed', error);
    syncedInMemory.delete(subject);
    return false;
  }
}

/** Mounted once in the shell: renders nothing, sends the claims once after sign-in. */
export function SessionClaimsSync({ op = SESSION_CLAIMS_SYNC }: { readonly op?: SessionClaimsSyncOp | null }): null {
  const { subject } = useAccessGrants();
  useEffect(() => {
    void syncClaimsOnce(op, subject);
  }, [op, subject]);
  return null;
}
